import * as _ from 'underscore';
import { isUndefined } from './utils';
import { DrEntity } from './dr-entity';
import { DrEntityAttribute } from './dr-entity-attribute';
import { DrObject, DrObjectType } from './dr-object';
import { ActionType, ObjectActionListType, ObjectActionSet } from './permission';
import { CellActionType, CellTemplateType } from './grid-context';
import { SettingsType } from './enums';

export class RoleViewModel extends DrObject {

    model: Partial<Role>;

    constructor(value: Partial<Role>) {
        super(value?.isSystemRole ? DrObjectType.SYSTEM_ROLE : DrObjectType.ROLE);
        if (value) {
            this.model = value;
        }
    }
}

export enum RolePermissionTabType {
    COMMON = 'Common',
    EDISCOVERY = 'eDiscovery',
    REVIEW = 'Review'
}

export enum SecureObjectGroupTypeLabels {
    DOCUMENT_ACTIONS = 'Document Actions',
    NAVIGATION_TREE = 'Navigation Tree Node',
    OTHER = 'Other',
    PANEL = 'Panel',
    PROJECT = 'Project',
    SETTINGS = 'Settings',
    COMMON_OTHER = 'Commom Other',

}

export enum SecureObjectGroupType {
    COMMON_OTHER = 'COMMON_OTHER',
    DOCUMENT_ACTIONS = 'DOCUMENT_ACTIONS',
    NAVIGATION_TREE = 'NAVIGATION_TREE',
    OTHER = 'OTHER',
    PANEL = 'PANEL',
    PROJECT = 'PROJECT',
    SETTINGS = 'SETTINGS',
    SYSTEM_ACTIONS = 'SYSTEM_ACTIONS',
    SYSTEM_SETTINGS = 'SYSTEM_SETTINGS',
    SYSTEM_CONFIGURATION = 'SYSTEM_CONFIGURATION',
    SYSTEM_PROVISIONING = 'SYSTEM_PROVISIONING'
}

export enum SecureObjectPermissionLevel {
    NONE = 'NONE',
    PROJECT = 'PROJECT',
    ORGANIZATION = 'ORGANIZATION',
    SYSTEM = 'SYSTEM',
    BOTH = 'BOTH'
}

export enum RoleAttributes {
    DEFAULT_ROLE_ATTRIBUTE = 'defaultRole'
}

export class Role extends DrEntity {
    public static noRoleHandle = 'NOROLEHANDLE';
    public static organizationAdministrator = 'Organization Administrator';
    public static systemAdministrator = 'System Administrator';
    public static systemITAdministrator = 'IT Administrator';

    name: string;
    objectActionList: Array<ObjectActionSet>;
    isSystemRole = false;

    settingsType: SettingsType;

    constructor(value?: Partial<Role>) {
        super(value);
        if (value) {
            Object.assign(this, value);
        }

        if (!isUndefined(this.name) && (this.name !== null)) {
            this.name.trim();
        }
    }

    isAdministratorRole(adminRoleName: string): boolean {
        return !isUndefined(this.name) && (this.name !== null) && (this.name === adminRoleName);
    }

    isDefault(): boolean {
        let isDefault = false;
        if (!isUndefined(this.attributes) && (this.attributes !== null)) {
            for (const attribute of this.attributes) {
                if (attribute.name === RoleAttributes.DEFAULT_ROLE_ATTRIBUTE) {
                    if (attribute.value === 'true') {
                        isDefault = true;
                    }
                    break;
                }
            }
        }
        return isDefault;
    }

    setDefault(defaultValue: boolean): void {
        const defaultValueString = defaultValue === false ? 'false' : 'true';
        if (isUndefined(this.attributes) || (this.attributes == null)) {
            this.attributes = new Array<DrEntityAttribute>();
        }

        const itemIndex = this.attributes.findIndex(item => item.name === RoleAttributes.DEFAULT_ROLE_ATTRIBUTE);
        if (itemIndex >= 0) {
            this.attributes[itemIndex].value = defaultValueString;
        } else {
            const defaultRoleAttribute = new DrEntityAttribute();
            defaultRoleAttribute.name = RoleAttributes.DEFAULT_ROLE_ATTRIBUTE;
            defaultRoleAttribute.value = defaultValueString;
            this.attributes.push(defaultRoleAttribute);
        }
    }

    copy(newName: string, isDefault: boolean, deep: boolean): Role {
        const newRole = new Role(this);
        newRole.handle = null;
        newRole.name = newName.trim();

        // copy attribute first
        newRole.attributes = new Array<DrEntityAttribute>();
        _.each(this.attributes, (attribute) => {
            const copiedAttribute = new DrEntityAttribute();
            copiedAttribute.name = attribute.name;
            copiedAttribute.value = attribute.value;
            newRole.attributes.push(copiedAttribute);
        });

        // now set default role, after attributes have been copied
        newRole.setDefault(isDefault);

        // copy object action sets, if deep = true, otherwise create a empty list.
        newRole.objectActionList = new Array<ObjectActionSet>();
        if (deep) {
            _.each(this.objectActionList, (details) => {
                const copiedList = new ObjectActionSet(details);
                newRole.objectActionList.push(copiedList);
            });
        }

        return newRole;
    }
}

export enum DirtyPermissionDetailFlags {
    PROJECT_VIEW = 0,
    PROJECT_DELETE,
    PROJECT_CREATE,
    ORGANIZATION_VIEW,
    ORGANIZATION_DELETE,
    ORGANIZATION_CREATE,
    SYSTEM_VIEW,
    SYSTEM_DELETE,
    SYSTEM_CREATE,
}

const NonTemplatesObjectActionList = ['SYSTEM_ROLE', ObjectActionListType.SYSTEM_GROUP, ObjectActionListType.SYSTEM_USER, ObjectActionListType.SYSTEM_MESSAGE, ObjectActionListType.SYSTEM_STORAGE, ObjectActionListType.SYSTEM_AUDIT_LOG]
export class PermissionDetail {

    bothPermissionsApplied: boolean;

    secureObjectType: ObjectActionListType;
    displayValue: string;
    iconClass: string;
    iconSecondClass: string;
    iconThirdClass: string;
    iconColor: string;
    iconThirdColor: string;
    sequence: number;
    cellTemplateType: CellTemplateType;
    isTemplate: boolean;
    // permission levels
    permissionLevel: any;
    projectViewState: boolean;
    projectDeleteState: boolean;
    projectCreateState: boolean;

    organizationViewState: boolean;
    organizationDeleteState: boolean;
    organizationCreateState: boolean;

    systemViewState: boolean;
    systemDeleteState: boolean;
    systemCreateState: boolean;

    isRoleHeader: boolean;

    constructor(value?: Partial<any>) {

        // used to track those permissions that have a SecureObjectPermissionLevel.BOTH
        // needed since we can know in what order the ORG_/PRJ_ permissions will come in
        // so we need to know when
        this.bothPermissionsApplied = false;

        if (value) {
            // display characteristics
            this.secureObjectType = value.secureObjectType;
            this.displayValue = value.displayValue === 'Profiles' ? 'Synthetic Documents' : value.displayValue === 'Organization' ? 'Organizations' : value.displayValue;
            this.iconClass = value.displayValue === 'Organization' ? 'fa-bank' : value.iconClass;
            this.iconColor = value.iconColor;
            this.sequence = value.sequence;

            // items with wrong icons
            if (this.secureObjectType === ObjectActionListType.MONITORING) {
                this.iconClass = 'fa-dashboard';
                this.iconColor = 'color-blue-01';
            }
            if (this.secureObjectType === ObjectActionListType.OCR_USAGE_REPORT) {
                this.iconClass = 'fa-list-alt';
                this.iconColor = 'color-blue-grey-03';
                this.iconThirdClass = 'fa-download';
                this.iconThirdColor = 'color-blue-01';
            }

            // items with more icons
            if (this.secureObjectType === ObjectActionListType.ACTIVE_USER_SESSIONS) {
                this.iconSecondClass = 'fa-clock-o';
            }
            if (this.secureObjectType === ObjectActionListType.PROJECT_ACCESS_LOG) {
                this.iconClass = 'fa-book';
                this.iconColor = 'color-grey-08';
                this.iconThirdClass = 'fa-history icon-position';
                this.iconThirdColor = 'color-purple-03';
            }
            if (this.secureObjectType === ObjectActionListType.SYSTEM_AUDIT_LOG) {
                this.iconClass = 'fa-gear';
                this.iconColor = 'color-grey-08';
                this.iconThirdClass = 'fa-history icon-position';
                this.iconThirdColor = 'color-purple-03';
            }
            if (this.secureObjectType === ObjectActionListType.SYSTEM_META_TEMPLATE) {
                this.iconSecondClass = 'fa-sticky-note';
                this.iconThirdClass = 'fa-check';
                this.iconThirdColor = 'color-white-01';
                this.isTemplate = true;
            }
            if (this.secureObjectType.startsWith('SYSTEM_') && !NonTemplatesObjectActionList.includes(this.secureObjectType)) {
                this.iconSecondClass = 'fa-sticky-note';
                this.isTemplate = true;
            }
            this
            // permission levels
            this.permissionLevel = value.permissionLevel;

            this.projectViewState = this.convertStringToBoolean(value.projectViewState);
            this.projectDeleteState = this.convertStringToBoolean(value.projectDeleteState);
            this.projectCreateState = this.convertStringToBoolean(value.projectCreateState);

            this.organizationViewState = this.convertStringToBoolean(value.organizationViewState);
            this.organizationDeleteState = this.convertStringToBoolean(value.organizationDeleteState);
            this.organizationCreateState = this.convertStringToBoolean(value.organizationDeleteState);

            this.systemViewState = this.convertStringToBoolean(value.systemViewState);
            this.systemDeleteState = this.convertStringToBoolean(value.systemDeleteState);
            this.systemCreateState = this.convertStringToBoolean(value.systemCreateState);
        }
    }

    convertStringToBoolean(stringValue: string): boolean {
        let boolValue = null;
        if (stringValue === 'true') {
            boolValue = true;
        } else if (stringValue === 'false') {
            boolValue = false;
        } else {
            boolValue = undefined;
        }
        return boolValue;
    }

    hasPermissionLevels(): boolean {
        let hasLevels = false;
        if (this.permissionLevel !== SecureObjectPermissionLevel.NONE) {
            hasLevels = true;
        }
        return hasLevels;
    }

    hasIcon(): boolean {
        let hasIconData = false;
        if ((this.iconClass != null) && (this.iconColor != null)) {
            hasIconData = true;
        }
        return hasIconData;
    }
}

export class RolePermissionViewData {

    groupsToPermissionsMap = new Map<SecureObjectGroupType, Array<ObjectActionListType>>();
    rolePermissionDetailViewDataMap = new Map<ObjectActionListType, PermissionDetail>();
    originalPermissionDetailMap = new Map<ObjectActionListType, PermissionDetail>();

    constructor(value?: Partial<any>) {

        let secureObjectGroupType = null;
        let permissionDetails = null;
        if (value) {
            _.each(value, (data) => {
                // settings group
                secureObjectGroupType = data.secureObjectGroupType;

                // display characteristics of each secureObjectTypes belonging to this group type
                if (data.permissionDetails) {
                    permissionDetails = new Array<ObjectActionListType>();
                    _.each(data.permissionDetails, (details) => {
                        const permissionDetail = new PermissionDetail(details);
                        permissionDetails.push(permissionDetail.secureObjectType);
                        // this map manages the changes made by the user.
                        this.rolePermissionDetailViewDataMap.set(permissionDetail.secureObjectType, permissionDetail);
                        this.originalPermissionDetailMap.set(permissionDetail.secureObjectType, permissionDetail);
                    });
                }

                // this map is used to setup the initial groups
                if ((permissionDetails !== null) && (!isUndefined(permissionDetails))) {
                    this.groupsToPermissionsMap.set(secureObjectGroupType, permissionDetails);
                }
            });
        }
    }

    hasActionType(typeToFind: ActionType, listToSearchForType: Array<ActionType>): boolean {
        let hasType = false;
        for (const action of listToSearchForType) {
            // these action types not used in 5.0
            if (typeToFind === ActionType.EDIT || typeToFind === ActionType.CREATE) { // edit = create in permissionDetails
                if ((action === ActionType.EDIT) || (action === ActionType.CREATE)) {
                    hasType = true;
                    break;
                }
            } else if (action === typeToFind) {
                hasType = true;
                break;
            }
        }
        return hasType;
    }


    applyRoleToPermissionDetails(objectActionLists: Array<ObjectActionSet>, role: Role): void {
        let currentDetails = null;
        let updateOrgPermissions = false;
        let projectType = null;
        let translatedSecureObjectType = null;

        if (objectActionLists) {

            _.each(objectActionLists, (actionObject) => {
                updateOrgPermissions = false;
                translatedSecureObjectType = actionObject.type;

                // We need to pull back the 'PRJ_' object and update the organization relate variables on that PermissionDetails.
                // note: permissionsDetails for SecureObjectPermissionLevel.BOTH use the PRJ_ secureObejctType name, not the organization type
                // name.  So we will use the objectActionList ORG_ object to populate the org variables of the 'PRJ_' permissions details.
                if (actionObject.type.toString().includes('ORG_') && (actionObject.type !== ObjectActionListType.ORG_WORKFLOWS) &&
                    (actionObject.type !== ObjectActionListType.ORG_TAGS) && (actionObject.type !== ObjectActionListType.PRJ_TAGS)) {
                    projectType = this.getProjectPermissionName(actionObject.type);
                    translatedSecureObjectType = ObjectActionListType[projectType];
                    updateOrgPermissions = true;
                }

                // get the permission details by type
                currentDetails = this.rolePermissionDetailViewDataMap.get(translatedSecureObjectType);

                // update the permissions details with the role's objectActionList
                if (currentDetails != null || !isUndefined(currentDetails)) {
                    let level = currentDetails.permissionLevel;
                    // traverse actions types (i.e. View, Edit, Delete) setting the permission details flags accordingly
                    _.each(ActionType, (actionType) => {
                        let state = false;
                        // determine the state of our permissionDetails (these are the one displayed for modification)
                        state = this.hasActionType(actionType, actionObject.actions);

                        // if both, we will want to process the project level first on the object, then organization level
                        if (updateOrgPermissions) {
                            level = SecureObjectPermissionLevel.ORGANIZATION;
                        }
                        currentDetails = this.updatePermissionDetail(translatedSecureObjectType, currentDetails, actionType, level, state);
                    });

                    // this map manages the changes made by the user.
                    this.rolePermissionDetailViewDataMap.set(translatedSecureObjectType, currentDetails);
                    this.originalPermissionDetailMap.set(translatedSecureObjectType, JSON.parse(JSON.stringify(currentDetails)));
                }
            });
            // IMPORTANT: If an objectActionType/secureObjectType is not in the PermissionDetails map, then it will be merged into the
            // final permission list in the end.  The objective this code is to work with PermissionDetails until it is time to save.
            // then correlate the changes from PermissionDetails with the orginalActionLists.
        }
    }

    getPermissionDetailsByGroup(secureObjectGroup: SecureObjectGroupType): Array<PermissionDetail> {
        let currentSecureObjectsByGroup = null;
        const permissionDetailsByGroup = new Array<PermissionDetail>();
        if (this.groupsToPermissionsMap) {
            currentSecureObjectsByGroup = this.groupsToPermissionsMap.get(secureObjectGroup);
        }

        if (currentSecureObjectsByGroup) {
            let permissionDetail = null;
            _.each(currentSecureObjectsByGroup, (secureObjectType) => {
                permissionDetail = this.rolePermissionDetailViewDataMap.get(secureObjectType);
                if (permissionDetail !== null) {
                    permissionDetailsByGroup.push(permissionDetail);
                }
            });
        }
        return permissionDetailsByGroup;
    }

    getPermissionDetail(secureObjectType: ObjectActionListType): PermissionDetail {
        let currentPermissionDetail = null;

        if (!isUndefined(this.rolePermissionDetailViewDataMap)) {
            currentPermissionDetail = this.rolePermissionDetailViewDataMap.get(secureObjectType);
        }
        return currentPermissionDetail;
    }

    updatePermissionDetail(secureObjectType: ObjectActionListType, currentDetails: PermissionDetail, actionType: string, level: SecureObjectPermissionLevel, state: boolean): PermissionDetail {
        // need to determine if we are setting the project or organization states when permissionDetails has both levels.
        if (currentDetails) {
            let stateByLevel = null;
            const mainLevels = [SecureObjectPermissionLevel.ORGANIZATION, SecureObjectPermissionLevel.PROJECT, SecureObjectPermissionLevel.SYSTEM];
            if (!mainLevels.includes(level)) {
                // set project level as default one
                level = SecureObjectPermissionLevel.PROJECT;
            }
            if (level)
                switch (actionType) {
                    case 'VIEW':
                        stateByLevel = `${level.toLowerCase()}ViewState`;
                        break;
                    case 'DELETE':
                        stateByLevel = `${level.toLowerCase()}DeleteState`;
                        break;
                    case 'CREATE':
                    case 'EDIT':
                        stateByLevel = `${level.toLowerCase()}CreateState`;
                        break;
                    default:
                        console.log('Error: updatePermissionDetail - actionType is <' + actionType + '> is not found.');
                        break;
                }

            if (!isUndefined(currentDetails[stateByLevel])) {
                currentDetails[stateByLevel] = state;
            }
            // propagate state to related permissions
            currentDetails = this.propagateState(currentDetails, actionType, level, state);

            // update the map with the new values
            this.rolePermissionDetailViewDataMap.set(secureObjectType, currentDetails);
        }

        // return the data
        return currentDetails;
    }

    propagateState(currentDetails: PermissionDetail, actionType: string, level: SecureObjectPermissionLevel, state: boolean): PermissionDetail {
        if (currentDetails) {
            const createStateByLevel = `${level.toLowerCase()}CreateState`;
            const viewStateByLevel = `${level.toLowerCase()}ViewState`;
            const deleteStateByLevel = `${level.toLowerCase()}DeleteState`;
            switch (actionType) {
                case 'VIEW':
                    {
                        // if view is false, all permissions to the right are false, as well.
                        if (state === false) {
                            if (!isUndefined(currentDetails[createStateByLevel])) {
                                currentDetails[createStateByLevel] = state;
                            }

                            if (!isUndefined(currentDetails[deleteStateByLevel])) {
                                currentDetails[deleteStateByLevel] = state;
                            }
                        }
                    }
                    break;
                case 'DELETE':
                    {
                        // if true, then add/edit and view become checked
                        if (state === true) {

                            if (!isUndefined(currentDetails[createStateByLevel])) {
                                currentDetails[createStateByLevel] = state;
                            }

                            if (!isUndefined(currentDetails[viewStateByLevel])) {
                                currentDetails[viewStateByLevel] = state;
                            }
                        }
                    }
                    break;
                case 'CREATE':
                case 'EDIT':
                    {
                        // if add/edit is false, all permissions to the right are false, as well.
                        // and view becomes checked, if not already.
                        if (state === false) {
                            if (!isUndefined(currentDetails[deleteStateByLevel])) {
                                currentDetails[deleteStateByLevel] = state;
                            }
                        } else {
                            if (!isUndefined(currentDetails[viewStateByLevel])) {
                                currentDetails[viewStateByLevel] = state;
                            }
                        }
                    }
                    break;
                default:
                    break;
            }
        }
        return currentDetails;
    }

    resetState(): void {
        this.originalPermissionDetailMap = new Map<ObjectActionListType, PermissionDetail>();
        this.rolePermissionDetailViewDataMap.forEach((value: PermissionDetail, key: ObjectActionListType) => {
            this.originalPermissionDetailMap.set(ObjectActionListType[key], JSON.parse(JSON.stringify(value)));
        });
    }

    isDirty(secureObjectType: ObjectActionListType, level: SecureObjectPermissionLevel): boolean {

        // compare current secure object states with original states
        const permissionDetail = this.rolePermissionDetailViewDataMap.get(secureObjectType);
        const originalPermissionDetail = this.originalPermissionDetailMap.get(secureObjectType);

        const createStateByLevel = `${level.toLowerCase()}CreateState`;
        const viewStateByLevel = `${level.toLowerCase()}ViewState`;
        const deleteStateByLevel = `${level.toLowerCase()}DeleteState`;

        if (permissionDetail && originalPermissionDetail) {
            return permissionDetail[createStateByLevel] !== originalPermissionDetail[createStateByLevel] ||
                permissionDetail[deleteStateByLevel] !== originalPermissionDetail[deleteStateByLevel] ||
                permissionDetail[viewStateByLevel] !== originalPermissionDetail[viewStateByLevel];
        } else {
            return false;
        }
    }

    getObjectActionList(originalRoleObjectActionMap: Map<string, ObjectActionSet>): ObjectActionSet[] {
        let finalPermissions = null;
        let allPermissionDetails = null;

        // Translated the permissionDetails that are displayed and may have been modified.
        if (!isUndefined(this.rolePermissionDetailViewDataMap) && (this.rolePermissionDetailViewDataMap.size > 0) && (!isUndefined(originalRoleObjectActionMap))) {

            allPermissionDetails = Array.from(this.rolePermissionDetailViewDataMap.values());
            finalPermissions = Array.from(originalRoleObjectActionMap.values());

            let objectActionList = null;
            _.each(allPermissionDetails, (permissionDetail) => {
                objectActionList = this.convertPermissionDetailsToObjectActionList(permissionDetail);

                // for those permissions that have only organization level
                // const clearActionList = this.clearOrganizationActions(permissionDetail, objectActionList);

                _.each(objectActionList, (itemToClear) => {
                    const itemIndex = finalPermissions.findIndex(item => item.type === itemToClear.type);
                    if (itemIndex > -1) {
                        finalPermissions[itemIndex].actions = itemToClear.actions;
                    }
                });
            });
        }

        return finalPermissions;
    }

    getOrganizationPermissionName(projectSecureObjectType: ObjectActionListType): string {
        let orgTypeName = '';
        if (projectSecureObjectType.includes('PRJ_')) {
            // remove PRJ from beginning and replace with 'ORG' text;
            orgTypeName = 'ORG' + projectSecureObjectType.slice(3, projectSecureObjectType.length);
        }
        return orgTypeName;
    }

    getProjectPermissionName(orgSecureObjectType: ObjectActionListType): string {
        let projectTypeName = '';
        if (orgSecureObjectType.includes('ORG_')) {
            // remove ORG from beginning and replace with 'PRJ' text;
            projectTypeName = 'PRJ' + orgSecureObjectType.slice(3, orgSecureObjectType.length);
        }
        return projectTypeName;
    }

    isOrganizationOnlyPermission(permissionDetail: PermissionDetail): boolean {
        let orgOnly = false;
        if (permissionDetail.permissionLevel === SecureObjectPermissionLevel.ORGANIZATION) {
            orgOnly = true;
        }
        return orgOnly;
    }

    convertPermissionDetailsToObjectActionList(permissionDetail: PermissionDetail): Array<ObjectActionSet> {
        const finalObjects = new Array<ObjectActionSet>();
        let finalPermission = new ObjectActionSet();
        finalPermission.type = permissionDetail.secureObjectType;
        finalPermission.actions = new Array<ActionType>();
        let currentCount = 0;

        const isOrganizationOnlyPermission = this.isOrganizationOnlyPermission(permissionDetail);

        if (permissionDetail.permissionLevel === SecureObjectPermissionLevel.PROJECT || permissionDetail.permissionLevel === SecureObjectPermissionLevel.BOTH) {
            // convert project related permissions
            if (this.hasViewPermissions(permissionDetail, true)) {
                finalPermission.actions.push(ActionType.VIEW);
            }

            if (this.hasDeletePermissions(permissionDetail, true)) {
                finalPermission.actions.push(ActionType.DELETE);
            }

            if (this.hasCreatePermissions(permissionDetail, true)) {
                finalPermission.actions.push(ActionType.CREATE);
                if (this.isToggleButton(finalPermission.type) === false) {
                    finalPermission.actions.push(ActionType.EDIT);
                }
            }

            currentCount = finalPermission.actions.length;
            if (currentCount > 0) {
                if (isUndefined(finalPermission.type)) {
                    console.log('Error: the type (PROJECT) is UNDEFINED for <' + permissionDetail.secureObjectType + '>');
                }
            }

            // store final objectActionList for this permission.
            finalObjects.push(finalPermission);
        }

        // This section of code handles those permission that are paired as PRJ_/ORG_, these are set as SecureObjectPermissionLevel.BOTH,
        // but passed up to the UI as one object that uses the PRJ_ SecureObjectTypes as its identifier.
        let organizationSecureObjectType = permissionDetail.secureObjectType.toString();
        if ((permissionDetail.secureObjectType.includes('PRJ_'))) {
            organizationSecureObjectType = this.getOrganizationPermissionName(permissionDetail.secureObjectType);
        }
        else if ((permissionDetail.secureObjectType.includes('SYSTEM_'))) {
            organizationSecureObjectType = this.getOrganizationPermissionName(permissionDetail.secureObjectType);
        }

        if (permissionDetail.permissionLevel === SecureObjectPermissionLevel.BOTH) {
            finalPermission = new ObjectActionSet();
            finalPermission.actions = new Array<ActionType>();
            finalPermission.type = <ObjectActionListType>organizationSecureObjectType;
            currentCount = 0;
        }

        const isSystemSettingsPermissions = permissionDetail.permissionLevel === SecureObjectPermissionLevel.SYSTEM;
        // convert organization/system related permissions
        if ((organizationSecureObjectType !== null) || isOrganizationOnlyPermission || isSystemSettingsPermissions) {

            if (this.hasViewPermissions(permissionDetail, false, isSystemSettingsPermissions)) {
                finalPermission.actions.push(ActionType.VIEW);
            }

            if (this.hasDeletePermissions(permissionDetail, false, isSystemSettingsPermissions)) {
                finalPermission.actions.push(ActionType.DELETE);
            }

            if (this.hasCreatePermissions(permissionDetail, false, isSystemSettingsPermissions)) {
                finalPermission.actions.push(ActionType.EDIT);
                finalPermission.actions.push(ActionType.CREATE);
            }

            currentCount = finalPermission.actions.length;
            if (currentCount > 0) {
                if (isUndefined(finalPermission.type)) {
                    console.log('Error: (ORG/SYSTEM) type is UNDEFINED for <' + permissionDetail.secureObjectType + '>');
                }
            }

            // update the organization related permission permissions
            finalObjects.push(finalPermission);
        }
        return finalObjects;
    }

    hasViewPermissions(permissionDetail: PermissionDetail, checkProjectPermissions: boolean, checkSystemPermissions?: boolean): boolean {
        let hasPermissions = false;
        if (checkProjectPermissions) {
            if (!isUndefined(permissionDetail.projectViewState) && (permissionDetail.projectViewState !== null) && (permissionDetail.projectViewState !== false)) {
                hasPermissions = true;
            }
        } else if (checkSystemPermissions) {
            if (!isUndefined(permissionDetail.systemViewState) && (permissionDetail.systemViewState !== null) && (permissionDetail.systemViewState !== false)) {
                hasPermissions = true;
            }
        } else if (!isUndefined(permissionDetail.organizationViewState) && (permissionDetail.organizationViewState !== null) && (permissionDetail.organizationViewState !== false)) {
            hasPermissions = true;
        }
        return hasPermissions;
    }

    hasDeletePermissions(permissionDetail: PermissionDetail, checkProjectPermissions: boolean, checkSystemPermissions?: boolean): boolean {
        let hasPermissions = false;
        if (checkProjectPermissions) {
            if (!isUndefined(permissionDetail.projectDeleteState) && (permissionDetail.projectDeleteState !== null) && (permissionDetail.projectDeleteState !== false)) {
                hasPermissions = true;
            }
        } else if (checkSystemPermissions) {
            if (!isUndefined(permissionDetail.systemDeleteState) && (permissionDetail.systemDeleteState !== null) && (permissionDetail.systemDeleteState !== false)) {
                hasPermissions = true;
            }
        } else if (!isUndefined(permissionDetail.organizationDeleteState) && (permissionDetail.organizationDeleteState !== null) && (permissionDetail.organizationDeleteState !== false)) {
            hasPermissions = true;
        }
        return hasPermissions;
    }

    hasCreatePermissions(permissionDetail: PermissionDetail, checkProjectPermissions: boolean, checkSystemPermissions?: boolean): boolean {
        let hasPermissions = false;
        if (checkProjectPermissions) {
            if (!isUndefined(permissionDetail.projectCreateState) && (permissionDetail.projectCreateState !== null) && (permissionDetail.projectCreateState !== false)) {
                hasPermissions = true;
            }
        } else if (checkSystemPermissions) {
            if (!isUndefined(permissionDetail.systemCreateState) && (permissionDetail.systemCreateState !== null) && (permissionDetail.systemCreateState !== false)) {
                hasPermissions = true;
            }
        } else if (!isUndefined(permissionDetail.organizationCreateState) && (permissionDetail.organizationCreateState !== null) && (permissionDetail.organizationCreateState !== false)) {
            hasPermissions = true;
        }
        return hasPermissions;
    }

    hasOrganizationPermissions(permissionDetail: PermissionDetail): boolean {
        let hasOrgPermissions = false;

        if (!isUndefined(permissionDetail.permissionLevel) && (permissionDetail.permissionLevel !== null)) {
            if (this.hasViewPermissions(permissionDetail, false)) {
                hasOrgPermissions = true;
            } else if (this.hasDeletePermissions(permissionDetail, false)) {
                hasOrgPermissions = true;
            } else if (this.hasCreatePermissions(permissionDetail, false)) {
                hasOrgPermissions = true;
            }
        }
        return hasOrgPermissions;
    }

    hasSystemPermissions(permissionDetail: PermissionDetail): boolean {
        let hasSystemPermissions = false;

        if (!isUndefined(permissionDetail.permissionLevel) && (permissionDetail.permissionLevel !== null)) {
            if (this.hasViewPermissions(permissionDetail, false, true)) {
                hasSystemPermissions = true;
            } else if (this.hasDeletePermissions(permissionDetail, false, true)) {
                hasSystemPermissions = true;
            } else if (this.hasCreatePermissions(permissionDetail, false, true)) {
                hasSystemPermissions = true;
            }
        }
        return hasSystemPermissions;
    }

    isToggleButton(secureObjectType: ObjectActionListType): boolean {

        let isToggle = true;

        switch (secureObjectType) {
            case ObjectActionListType.DOCUMENT_TAGS:
            case ObjectActionListType.DOCUMENT_FOLDER:
            case ObjectActionListType.DOCUMENT_CUSTODIAN:
            case ObjectActionListType.DOWNLOAD_NATIVES:
            case ObjectActionListType.DOWNLOAD_PDF:
            case ObjectActionListType.AUDIT_LOG:
            case ObjectActionListType.DOCUMENT_REPORTS:
            case ObjectActionListType.LOCK_UNLOCK_WORKFLOW:
            case ObjectActionListType.CONNECTOR_ACCESS:
            case ObjectActionListType.PROJECT_MONITORING_ACCESS:
                break;
            default:
                {
                    isToggle = false;
                }
                break;
        }

        return isToggle;
    }
}
